import React, { useState, useCallback } from 'react';
import { PaymentFormData, TransferDetail, ModalBadgeProps } from '../../types/modal.types';
import { useLanguage } from '../../context/LanguageContext';
import ModalButton from '../UI/ModalButton';
import Toast from '../UI/Toast';

interface PaymentFormProps {
  initialData?: Partial<PaymentFormData>;
  onSubmit: () => void;
  onCancel?: () => void;
  confirming?: boolean;
  error?: string | null;
  paymentStatus?: string;
  timeExpired?: boolean;
  badge?: ModalBadgeProps;
}

const formatCardNumber = (card: string): string => {
  const cleaned = card.replace(/\D/g, '');
  const groups = cleaned.match(/.{1,4}/g);
  return groups ? groups.join(' ') : card;
};

const isCardNumber = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 16;
};

const PaymentForm: React.FC<PaymentFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  confirming = false,
  error,
  paymentStatus,
  timeExpired = false,
  badge
}) => {
  const { t } = useLanguage();
  const [showToast, setShowToast] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  const formattedCard = initialData.cardNumber ? formatCardNumber(initialData.cardNumber) : '';
  const requisiteLabel = isCardNumber(initialData.cardNumber || '') ? t('cardNumber') : t('phoneNumber');

  const transferDetails: TransferDetail[] = [
    { label: t('bankName'), value: initialData.bankName || '' },
    { label: requisiteLabel, value: formattedCard },
    { label: t('accountHolder'), value: initialData.accountHolder || '' },
  ].filter(detail => detail.value);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirming && !timeExpired) {
      onSubmit();
    }
  };

  const handleCopy = async (value: string) => {
    try {
      const rawValue = value.replace(/[\s\-()]/g, '');
      await navigator.clipboard.writeText(rawValue);
      setShowToast(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleToastClose = useCallback(() => {
    setShowToast(false);
  }, []);

  const getCopyIcon = () => {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="9" y="9" width="13" height="13" rx="2" stroke="#9CA3AF" strokeWidth="2"/>
        <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#9CA3AF" strokeWidth="2"/>
      </svg>
    );
  };

  const getButtonText = () => {
    if (confirming) return t('processing');
    if (timeExpired) return t('timeExpired');
    if (paymentStatus === 'confirmed') return t('paymentConfirmed');
    return t('sentMoney');
  };

  const isButtonDisabled = confirming || timeExpired || paymentStatus === 'confirmed';

  return (
    <>
      <Toast
        message={t('successfullyCopied')}
        visible={showToast}
        onClose={handleToastClose}
      />

      <form className="payment-form" onSubmit={handleSubmit}>
        <div className="transfer-details">
          <h3 className="transfer-details__title">{t('copyTransferDetails')}</h3>

          <div className="transfer-details__list">
            {transferDetails.map((detail, index) => (
              <div key={index} className="transfer-detail">
                <div className="transfer-detail__content">
                  <span className="transfer-detail__label">{detail.label}</span>
                  <span className="transfer-detail__value">{detail.value}</span>
                </div>
                <div className="transfer-detail__copy-wrapper">
                  <button
                    type="button"
                    className="transfer-detail__copy"
                    onClick={() => handleCopy(detail.value)}
                    onMouseEnter={() => setHoveredButton(detail.label)}
                    onMouseLeave={() => setHoveredButton(null)}
                  >
                    {getCopyIcon()}
                  </button>
                  {hoveredButton === detail.label && (
                    <div className="transfer-detail__tooltip">
                      {t('copy')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="payment-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {badge && (
          <div className="payment-form__status">
            <div className="payment-form__status-left">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#111827" strokeWidth="2"/>
                <path d="M12 6V12L16 14" stroke="#111827" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{t('waitingForTransfer')}</span>
            </div>
            <div className={`payment-form__timer ${badge.variant === 'danger' ? 'payment-form__timer--danger' : ''}`}>
              {badge.time}
            </div>
          </div>
        )}

        <div className="payment-form__actions">
          <p className="payment-form__hint">
            {t('sendExactAmount')}
          </p>

          <ModalButton
            variant="primary"
            type="submit"
            disabled={isButtonDisabled}
          >
            {getButtonText()}
          </ModalButton>

          {onCancel && (
            <ModalButton
              variant="outline"
              type="regular"
              onClick={onCancel}
              disabled={confirming}
            >
              {t('cancelPayment')}
            </ModalButton>
          )}
        </div>
      </form>
    </>
  );
};

export default PaymentForm;
